/**
 * Vault Routes Configuration
 *
 * Single source of truth for all MedCredVault (vault) domain routes.
 * This config drives:
 * - React Router route definitions
 * - vaultState machine pageKey constants
 * - Navigation sidebar links
 * - Cypress test tags
 *
 * Vault domain (medcredvault.com) handles:
 * - Provider profile management
 * - Documents, licenses, certifications
 * - Academics, employment, privileges
 * - All provider data sections
 */

import CT from '@marketplace-ai/tags';
import type { ComponentType } from 'react';
import { AcademicDetails } from '~/features/vault/academics';
import { AOIDetails } from '~/features/vault/aoi';
import { AttestationDetail } from '~/features/vault/attestations';
import { BoardCertDetails } from '~/features/vault/board-certification';
import { CertificationDetails } from '~/features/vault/certification';
import { CMETrainingDetails } from '~/features/vault/cme-training';
import { Dashboard } from '~/features/vault/dashboard/Dashboard';
import { DocumentsView } from '~/features/vault/documents/DocumentsView';
import { EmploymentDetails } from '~/features/vault/employment';
import { LicensesFormWrapper } from '~/features/vault/licenses';
import { MalpracticeView } from '~/features/vault/malpractice';
import { MedicalLicenseDetails } from '~/features/vault/medical-license';
import { PeerRefReqDetails } from '~/features/vault/peer-reference-request';
import { PrivilegesView } from '~/features/vault/privileges';
import { ProfileForm } from '~/features/vault/profile/ProfileForm';
import { Settings } from '~/pages/settings/Settings';
import { paths, PROVIDER_PAGE_KEYS } from './routes';

/**
 * Vault route configuration interface
 */
export interface VaultRouteConfig {
  // Route definition
  path: string; // URL path
  pageKey: string; // Page key used by vaultState ON_SET_PAGE events
  stateView: string; // Target state in vaultState machine

  // Component
  component: ComponentType; // React component to render
  wrapper?: ComponentType; // Optional wrapper (e.g., provider components)

  // Navigation metadata
  label: string; // Display name for navigation
  section?: string; // Section grouping for sidebar organization
  description?: string; // Tooltip or help text
  icon?: string; // Icon emoji or component

  // Sub-route/tab support
  activeTab?: string; // Default active tab for tabbed pages
  isDefaultTab?: boolean; // Marks the entry shown when the URL has no tab hash
  tabState?: number; // Default tab index

  // Testing
  cyTag?: string; // Cypress test tag

  // Access control
  requiresProviderAuth: boolean; // Requires authenticated provider
}

/**
 * Complete vault routes configuration
 *
 * Order matters here:
 * - First route = default landing for authenticated providers
 * - Routes are grouped by section for navigation
 */
export const vaultRoutes: VaultRouteConfig[] = [
  // ====================
  // Profile Section
  // ====================
  {
    path: '/vault/dashboard',
    pageKey: 'dashboard',
    stateView: '$_DASHBOARD_VIEW',
    component: Dashboard,
    label: 'Dashboard',
    section: 'Profile',
    description: 'Overview of your MedCredVault profile',
    icon: '🏠',
    cyTag: CT?.layout.navDrawer.dashboardLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/profile',
    pageKey: 'profile',
    stateView: '$_PROFILE_FORM',
    component: ProfileForm,
    label: 'Personal Profile',
    section: 'Profile',
    description: 'Manage your personal information',
    icon: '👤',
    cyTag: CT.layout.navDrawer.profileLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/aoi',
    pageKey: 'aoi',
    stateView: '$_AOI_VIEW',
    component: AOIDetails,
    label: 'Areas of Interest',
    section: 'Profile',
    description: 'Manage your areas of interest',
    icon: '🏷️',
    cyTag: CT.layout.navDrawer.aoiLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/settings',
    pageKey: PROVIDER_PAGE_KEYS.settings,
    stateView: '$_SETTINGS_VIEW',
    component: Settings,
    label: 'Settings',
    section: 'Profile',
    description: 'Account settings',
    icon: '⚙️',
    cyTag: CT.layout.navDrawer.settingsLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/employment',
    pageKey: PROVIDER_PAGE_KEYS.employment,
    stateView: '$_EMPLOYMENT_VIEW',
    component: EmploymentDetails,
    label: 'Employment',
    section: 'Employment',
    description: 'Employment history and positions',
    icon: '💼',
    cyTag: CT.layout.navDrawer.employmentHistory || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/academics',
    pageKey: PROVIDER_PAGE_KEYS.academics,
    stateView: '$_ACADEMICS_VIEW',
    component: AcademicDetails,
    label: 'Education',
    section: 'Academics and Training',
    description: 'Academic background and education',
    activeTab: 'education',
    isDefaultTab: true,
    icon: '🎓',
    cyTag: CT.layout.navDrawer.educationLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/academics',
    pageKey: PROVIDER_PAGE_KEYS.academics,
    stateView: '$_ACADEMICS_VIEW',
    component: AcademicDetails,
    label: 'Training',
    section: 'Academics and Training',
    description: 'Academic background and education',
    activeTab: 'training',
    icon: '🎓',
    cyTag: CT.layout.navDrawer.trainingLink || '',
    requiresProviderAuth: true,
  },
  {
    // NOTE: this needs to be updated across the nav menu
    // path: '/vault/medical-license',
    path: paths.vault.medicalLicense,
    pageKey: PROVIDER_PAGE_KEYS.medicalLicense,
    stateView: '$_MEDICAL_LICENSE_VIEW',
    component: MedicalLicenseDetails,
    label: 'Medical Licenses',
    section: 'Licenses',
    description: 'Medical license information',
    icon: '💉',
    cyTag: CT.layout.navDrawer.medicalLicenseLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/licenses',
    pageKey: PROVIDER_PAGE_KEYS.licenses,
    stateView: '$_LICENSES_VIEW',
    component: LicensesFormWrapper,
    label: 'DEA Licenses',
    activeTab: 'dea',
    isDefaultTab: true,
    section: 'Licenses',
    description: 'View and manage professional licenses',
    icon: '📜',
    cyTag: CT.layout.navDrawer.deaLicenseLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/licenses',
    pageKey: PROVIDER_PAGE_KEYS.licenses,
    stateView: '$_LICENSES_VIEW',
    component: LicensesFormWrapper,
    label: 'CDS Licenses',
    activeTab: 'cds',
    section: 'Licenses',
    description: 'View and manage professional licenses',
    icon: '📜',
    cyTag: CT.layout.navDrawer.cdsLicenseLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/board-certifications',
    pageKey: PROVIDER_PAGE_KEYS.boardCertifications,
    stateView: '$_BOARD_CERT_VIEW',
    component: BoardCertDetails,
    label: 'Board Certifications',
    section: 'Certifications',
    description: 'Board certifications',
    icon: '🎖️',
    cyTag: CT.layout.navDrawer.boardCertificationsLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/certifications',
    pageKey: PROVIDER_PAGE_KEYS.certifications,
    stateView: '$_CERT_VIEW',
    component: CertificationDetails,
    label: 'Certifications',
    section: 'Certifications',
    description: 'Other certifications and credentials',
    icon: '📋',
    cyTag: CT.layout.navDrawer.certificationsLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/cme-training',
    pageKey: PROVIDER_PAGE_KEYS.cmeTraining,
    stateView: '$_CME_TRAINING_VIEW',
    component: CMETrainingDetails,
    label: 'CME Training',
    section: 'CME',
    description: 'CME training and credits',
    icon: '📚',
    cyTag: CT.layout.navDrawer.cmeLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/privileges',
    pageKey: PROVIDER_PAGE_KEYS.privileges,
    stateView: '$_PRIVILEGES_VIEW',
    component: PrivilegesView,
    label: 'Privileges',
    section: 'Privileges',
    description: 'Hospital privileges and affiliations',
    icon: '🏥',
    cyTag: CT.layout.navDrawer.privilegesLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/malpractice',
    pageKey: PROVIDER_PAGE_KEYS.malpractice,
    stateView: '$_MALPRACTICE_VIEW',
    component: MalpracticeView,
    label: 'Malpractice',
    section: 'Insurance',
    description: 'Malpractice claims and insurance',
    icon: '⚖️',
    cyTag: CT.layout.navDrawer.malpracticeInsuranceLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/attestations',
    pageKey: PROVIDER_PAGE_KEYS.attestations,
    stateView: '$_ATTESTATIONS_VIEW',
    component: AttestationDetail,
    label: 'Attestations',
    section: 'Documents',
    description: 'Attestations and verifications',
    icon: '✍️',
    cyTag: CT.layout.navDrawer.attestationLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/peer-references',
    pageKey: PROVIDER_PAGE_KEYS.peerReferences,
    stateView: '$_PEER_REFERENCES_VIEW',
    component: PeerRefReqDetails,
    label: 'Peer References',
    section: 'Documents',
    description: 'Peer references',
    icon: '👥',
    cyTag: CT.layout.navDrawer.referencesLink || '',
    requiresProviderAuth: true,
  },
  {
    path: '/vault/documents',
    pageKey: PROVIDER_PAGE_KEYS.documents,
    stateView: '$_DOCUMENTS_VIEW',
    component: DocumentsView,
    // wrapper: DocumentsViewProvider,
    label: 'Documents',
    section: 'Documents',
    description: 'Upload and manage documents',
    icon: '📄',
    cyTag: CT.layout.navDrawer.documentsLink || '',
    requiresProviderAuth: true,
  },
];

// ====================
// Section Ordering
// ====================

export const VAULT_SECTION_ORDER = [
  'Profile',
  'Employment',
  'Academics and Training',
  'Licenses',
  'Certifications',
  'CME',
  'Privileges',
  'Insurance',
  'Documents',
] as const;

export type VaultSection = (typeof VAULT_SECTION_ORDER)[number];

// ====================
// Derived Exports
// ====================

/**
 * Route paths object for type-safe navigation
 */
export const vaultPaths = vaultRoutes.reduce(
  (acc, route) => {
    // Extract route name from path (e.g., '/vault/dashboard' -> 'dashboard')
    const routeName = route.path.split('/').pop() as string;
    return {
      ...acc,
      [routeName]: route.path,
    };
  },
  {} as Record<string, string>
);

/**
 * Page key to state view mapping for vaultState
 * Used to simplify ON_SET_PAGE event handlers
 */
export const pageKeyToStateView: Record<string, string> = vaultRoutes.reduce(
  (acc, route) => {
    return {
      ...acc,
      [route.pageKey]: route.stateView,
    };
  },
  {} as Record<string, string>
);

/**
 * State view to page key mapping (reverse lookup)
 */
export const stateViewToPageKey: Record<string, string> = vaultRoutes.reduce(
  (acc, route) => {
    return {
      ...acc,
      [route.stateView]: route.pageKey,
    };
  },
  {} as Record<string, string>
);

/**
 * Routes grouped by section for navigation components
 * Returns array of { section, routes } objects in defined order
 */
export const vaultRoutesBySection: Array<{
  section: string;
  routes: VaultRouteConfig[];
}> = VAULT_SECTION_ORDER.map((section) => ({
  section,
  routes: vaultRoutes.filter((route) => route.section === section),
})).filter((group) => group.routes.length > 0);

/**
 * Get route config by path
 */
export function getVaultRouteByPath(path: string): VaultRouteConfig | undefined {
  return vaultRoutes.find((route) => route.path === path);
}

/**
 * Get route config by pageKey
 */
export function getVaultRouteByPageKey(pageKey: string): VaultRouteConfig | undefined {
  return vaultRoutes.find((route) => route.pageKey === pageKey);
}

/**
 * Get route config by stateView
 */
export function getVaultRouteByStateView(stateView: string): VaultRouteConfig | undefined {
  return vaultRoutes.find((route) => route.stateView === stateView);
}

/**
 * Get default vault route (dashboard)
 */
export function getDefaultVaultRoute(): VaultRouteConfig {
  return vaultRoutes[0]; // Dashboard is first
}

// ====================
// Types
// ====================
export type VaultPath = (typeof vaultPaths)[keyof typeof vaultPaths];
export type VaultPageKey = typeof pageKeyToStateView extends Record<infer K, string> ? K : never;
export type VaultStateView = typeof stateViewToPageKey extends Record<infer K, string> ? K : never;
